import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import ProductGallery from '../components/ProductGallery';
import ProductReviews from '../components/ProductReviews';
import PriceHistory from '../components/PriceHistory';
import LastSold from '../components/LastSold';
import MarketCompare from '../components/MarketCompare';
import FrequentlyBoughtTogether from '../components/FrequentlyBoughtTogether';
import { useToast } from '../components/ui';
import useDocumentMeta from '../hooks/useDocumentMeta';
import useApprovalStatus from '../hooks/useApprovalStatus';
import usePurchaseLimit from '../hooks/usePurchaseLimit';
import useResolvedPrice from '../hooks/useResolvedPrice';
import useFeatureFlags from '../hooks/useFeatureFlags';
import { Link } from 'react-router-dom';
import './ProductDetail.css';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const toast = useToast();
  const { isApproved, isLoggedIn } = useApprovalStatus();
  const flags = useFeatureFlags();
  const loyaltyDiscountsOn = flags.loyalty_tier_discounts_enabled === true;
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const limit = usePurchaseLimit(isApproved ? id : null);
  const resolved = useResolvedPrice(isApproved ? id : null, quantity);
  const [selectedVariantId, setSelectedVariantId] = useState(null);
  const [jsonLd, setJsonLd] = useState(null);
  const [breadcrumb, setBreadcrumb] = useState(null);

  useEffect(() => {
    Promise.all([
      fetch(`/api/seo/jsonld/product/${id}`).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`/api/seo/jsonld/breadcrumb/product/${id}`).then(r => r.ok ? r.json() : null).catch(() => null),
    ]).then(([p, b]) => { setJsonLd(p); setBreadcrumb(b); });
  }, [id]);

  useEffect(() => {
    fetch(`/api/products/${id}`)
      .then(res => res.json())
      .then(data => {
        setProduct(data);
        // Pick first in-stock variant by default; else first variant
        if (data.variants && data.variants.length) {
          const inStock = data.variants.find(v => v.stock > 0) || data.variants[0];
          setSelectedVariantId(inStock.id);
        }
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching product:', err);
        setLoading(false);
      });
  }, [id]);

  // Effective product = base merged with selected variant (if any)
  const variant = product?.variants?.find(v => v.id === selectedVariantId) || null;
  const effective = product && (variant ? {
    ...product,
    variant_id: variant.id,
    condition: variant.variant_value || product.condition,
    price: (product.price || 0) + (variant.price_adjustment || 0),
    stock: variant.stock,
    sku: variant.sku,
  } : product);

  // Tier 2 Item 22 — modifiers
  const [modifiers, setModifiers] = useState([]);
  const [modifierValues, setModifierValues] = useState({});
  useEffect(() => {
    if (!product?.id) return;
    fetch(`/api/products/${product.id}/modifiers`)
      .then(r => r.ok ? r.json() : [])
      .then(d => setModifiers(Array.isArray(d) ? d : []))
      .catch(() => setModifiers([]));
  }, [product?.id]);

  const validateModifiers = () => {
    for (const m of modifiers) {
      if (m.required && !String(modifierValues[m.label] || '').trim()) {
        toast.error(`"${m.label}" is required`);
        return false;
      }
    }
    return true;
  };
  const modifierSurchargeCents = modifiers.reduce((s, m) =>
    s + (modifierValues[m.label] ? Number(m.surcharge_cents || 0) : 0), 0);

  const handleAddToCart = () => {
    if (!validateModifiers()) return;
    const payload = { ...effective };
    if (Object.keys(modifierValues).length) payload.modifiers = { ...modifierValues };
    if (modifierSurchargeCents > 0) {
      payload.price = Number(effective.price) + modifierSurchargeCents / 100;
      payload.modifier_surcharge_cents = modifierSurchargeCents;
    }
    addToCart(payload, quantity);
    toast.success(`${quantity} × ${effective.name}${variant ? ` (${variant.variant_value})` : ''} added to cart`);
  };

  const handleBuyNow = () => {
    if (!validateModifiers()) return;
    handleAddToCart();
    navigate('/cart');
  };

  // Sprint 2.5: pre-auth deposit CTA. When the product has preauth_enabled,
  // we add it to the cart tagged so checkout authorizes the deposit amount
  // instead of capturing the full price (e.g. drops / pre-orders like Pitch Black).
  const handleReserveWithDeposit = () => {
    if (!validateModifiers()) return;
    const payload = { ...effective, preauth_mode: true, preauth_amount: Number(product.preauth_amount || 0) };
    if (Object.keys(modifierValues).length) payload.modifiers = { ...modifierValues };
    addToCart(payload, quantity);
    toast.success(`${quantity} × ${effective.name} reserved with $${Number(product.preauth_amount || 0).toFixed(2)} deposit`);
    navigate('/cart');
  };

  // Inject SEO/meta when product is loaded (hook must run on every render)
  useDocumentMeta(product ? {
    title: `${product.name} — SilverGames`,
    description: product.description || `${product.name} • ${product.set_name || ''} • ${product.condition || ''}`,
    image: product.image_url,
    url: `${window.location.origin}/product/${product.id}`,
    type: 'product',
    jsonLd: [jsonLd, breadcrumb].filter(Boolean),
  } : {});

  if (loading) {
    return <div className="loading">Loading product details...</div>;
  }

  if (!product) {
    return <div className="error">Product not found</div>;
  }

  return (
    <div className="product-detail">
      <div className="product-detail-grid">
        <div className="product-detail-image">
          <ProductGallery
            images={
              (product.images && product.images.length > 0)
                ? product.images
                : (product.image_url
                    ? [{ image_url: product.image_url, alt_text: product.name }]
                    : (product.tcg_card_id
                        ? (() => {
                            const dash = product.tcg_card_id.lastIndexOf('-');
                            if (dash <= 0) return [];
                            const setId = product.tcg_card_id.slice(0, dash);
                            const num = product.tcg_card_id.slice(dash + 1);
                            return [{
                              image_url: `https://images.pokemontcg.io/${setId}/${num}_hires.png`,
                              alt_text: product.name,
                            }];
                          })()
                        : []))
            }
            productName={product.name}
          />
        </div>

        <div className="product-detail-info">
          <h1>{product.name}</h1>

          {product.set_name && (
            <div className="product-meta">
              <span className="meta-label">Set:</span>
              <span className="meta-value">{product.set_name}</span>
            </div>
          )}

          {product.card_number && (
            <div className="product-meta">
              <span className="meta-label">Card Number:</span>
              <span className="meta-value">{product.card_number}</span>
            </div>
          )}

          {product.rarity && (
            <div className="product-meta">
              <span className="meta-label">Rarity:</span>
              <span className={`rarity-badge rarity-${product.rarity.toLowerCase().replace(/\s+/g, '-')}`}>
                {product.rarity}
              </span>
            </div>
          )}

          {product.variants && product.variants.length > 0 ? (
            <div className="variant-selector">
              <span className="meta-label">Choose condition</span>
              <div className="variant-pills">
                {product.variants.map(v => {
                  // Sprint A: variant.sell_price (when set) is the authoritative
                  // price. Falls back to base + price_adjustment for legacy
                  // variants that didn't get a per-condition absolute price.
                  const total = v.sell_price != null
                    ? Number(v.sell_price)
                    : ((product.price || 0) + (v.price_adjustment || 0));
                  // Prefer the explicit condition + finish columns (Sprint A);
                  // fall back to the legacy variant_value pipe-format.
                  const label = v.condition
                    ? `${v.condition}${v.finish && v.finish !== 'non-foil' ? ` ${v.finish}` : ''}`
                    : v.variant_value;
                  return (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => { setSelectedVariantId(v.id); setQuantity(1); }}
                      disabled={v.stock <= 0}
                      className={`variant-pill${selectedVariantId === v.id ? ' variant-pill--active' : ''}${v.stock <= 0 ? ' variant-pill--oos' : ''}`}
                      title={v.stock <= 0 ? 'Out of stock' : `${v.stock} in stock`}
                    >
                      <span className="variant-pill__name">{label}</span>
                      <span className="variant-pill__price">${total.toFixed(2)}</span>
                      <span className="variant-pill__stock">
                        {v.stock <= 0 ? 'Sold out' : v.stock < 5 ? `${v.stock} left` : 'In stock'}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (product.condition && (
            <div className="product-meta">
              <span className="meta-label">Condition:</span>
              <span className="meta-value">{product.condition}</span>
            </div>
          ))}

          {product.category_name && (
            <div className="product-meta">
              <span className="meta-label">Category:</span>
              <span className="meta-value">{product.category_name}</span>
            </div>
          )}

          <div className="product-price-section">
            {/* Sprint 5: server-stripped price (price_gate_anon flag) takes
                precedence — never render $null even if isApproved is true. */}
            {isApproved && !product.price_gated ? (
              <>
                {product.compare_at_price && Number(product.compare_at_price) > Number(effective.price) && (
                  <span style={{ marginRight: 8, color: 'var(--text-muted)', textDecoration: 'line-through' }}>
                    ${Number(product.compare_at_price).toFixed(2)}
                  </span>
                )}
                <span className="product-detail-price"
                      style={product.compare_at_price && Number(product.compare_at_price) > Number(effective.price)
                        ? { color: 'var(--color-danger)' } : {}}>
                  ${Number(resolved?.final ?? effective.price).toFixed(2)}
                </span>
                <MarketCompare
                  price={resolved?.final ?? effective.price}
                  marketPrice={product.market_price}
                  marketPriceAt={product.market_price_at}
                  variant="detail"
                />
                {resolved?.group_price_applied && resolved.final < resolved.base && (
                  <div style={{ marginTop: 6, fontSize: 13, color: 'var(--color-success, #16a085)' }}>
                    <span style={{ display: 'inline-block', padding: '2px 8px', background: '#0d5c3a', color: '#fff', borderRadius: 4, fontSize: 11, fontWeight: 700, marginRight: 6 }}>WHOLESALE</span>
                    <s style={{ color: 'var(--text-light)' }}>${Number(resolved.base).toFixed(2)}</s>
                    {' '}<strong>{resolved.group?.name} price</strong> — saving ${(resolved.base - resolved.final).toFixed(2)}
                  </div>
                )}
                {!resolved?.group_price_applied && resolved?.tier && resolved.tier.discount_pct > 0 && resolved.final < resolved.base && (
                  <div style={{ marginTop: 6, fontSize: 13, color: 'var(--color-success, #16a085)' }}>
                    <s style={{ color: 'var(--text-light)' }}>${Number(resolved.base).toFixed(2)}</s>
                    {' '}
                    <strong>Your {resolved.tier.name} tier price</strong> — saving ${(resolved.base - resolved.final).toFixed(2)} ({resolved.tier.discount_pct}% off)
                  </div>
                )}
                {/* R4 — loyalty-tier member price (silver/gold/platinum % off) */}
                {resolved?.loyalty_tier && resolved.loyalty_tier.discount_pct > 0 && resolved.final < resolved.base && (
                  <div style={{ marginTop: 6, fontSize: 13, color: 'var(--color-success, #16a085)' }}>
                    <span style={{ display: 'inline-block', padding: '2px 8px', background: '#0d5c3a', color: '#fff', borderRadius: 4, fontSize: 11, fontWeight: 700, marginRight: 6 }}>
                      {resolved.loyalty_tier.name.toUpperCase()}
                    </span>
                    <s style={{ color: 'var(--text-light)' }}>${Number(resolved.base).toFixed(2)}</s>
                    {' '}<strong>{resolved.loyalty_tier.name} member price</strong> — saving ${(resolved.base - resolved.final).toFixed(2)} ({resolved.loyalty_tier.discount_pct}% off)
                  </div>
                )}
                {/* R4 — acquisition teaser for signed-out shoppers when the perk is live */}
                {!isLoggedIn && !resolved?.loyalty_tier && loyaltyDiscountsOn && (
                  <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text-light)' }}>
                    💳 Loyalty members save storewide — <Link to={`/register?next=${encodeURIComponent(window.location.pathname)}`}>join free</Link>.
                  </div>
                )}
                {effective.stock > 0 && effective.stock < 10 && (
                  <span className="stock-warning">Only {effective.stock} left in stock!</span>
                )}
                {effective.stock <= 0 && (
                  <span className="out-of-stock">Out of Stock</span>
                )}
                {limit?.most_restrictive && (
                  <div style={{ marginTop: 8, fontSize: 13, color: 'var(--text-light)', padding: '6px 10px', background: 'var(--bg-hover)', borderRadius: 4, display: 'inline-block' }}>
                    {limit.most_restrictive.remaining > 0 ? (
                      <>Limit: <strong>{limit.most_restrictive.max}</strong> per customer · <strong>{limit.most_restrictive.remaining}</strong> remaining for you</>
                    ) : (
                      <span style={{ color: 'var(--color-danger)' }}>You've reached your lifetime limit ({limit.most_restrictive.max}) for this {limit.most_restrictive.scope}.</span>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div style={{ padding: 16, background: 'var(--bg-hover, #f1f5f9)', borderRadius: 8, border: '1px dashed var(--border-color)' }}>
                <span className="product-detail-price" style={{ filter: 'blur(6px)', userSelect: 'none' }} aria-hidden="true">$••.••</span>
                <p style={{ marginTop: 12, color: 'var(--text-light)', fontSize: 14 }}>
                  {isLoggedIn ? 'Pricing is shown to approved accounts only.' : 'Sign in to view pricing.'}
                </p>
                <Link
                  to={isLoggedIn ? '/apply' : `/login?next=${encodeURIComponent(window.location.pathname)}`}
                  className="btn btn-primary"
                  style={{ marginTop: 8, display: 'inline-block' }}
                >
                  {isLoggedIn ? 'Apply for access →' : 'Sign in →'}
                </Link>
                {!isLoggedIn && (
                  <Link to="/register" style={{ marginLeft: 12, fontSize: 14 }}>
                    or create an account
                  </Link>
                )}
              </div>
            )}
          </div>

          {product.description && (
            <div className="product-description">
              <h3>Description</h3>
              <p>{product.description}</p>
            </div>
          )}

          {effective.stock > 0 && isApproved && !product.price_gated && (
            limit?.most_restrictive && limit.most_restrictive.remaining <= 0 ? (
              <div className="product-actions">
                <p style={{ color: 'var(--color-danger)', fontWeight: 600 }}>
                  You've reached your lifetime purchase limit on this {limit.most_restrictive.scope}.
                  Contact support if you need an exception.
                </p>
              </div>
            ) : (
            <div className="product-actions">
              <div className="quantity-selector">
                <label>Quantity:</label>
                <div className="quantity-controls">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="qty-btn"
                  >
                    -
                  </button>
                  <input
                    type="number"
                    value={quantity}
                    onChange={(e) => {
                      const cap = limit?.most_restrictive ? Math.min(effective.stock, limit.most_restrictive.remaining) : effective.stock;
                      setQuantity(Math.max(1, Math.min(cap, parseInt(e.target.value) || 1)));
                    }}
                    min="1"
                    max={limit?.most_restrictive ? Math.min(effective.stock, limit.most_restrictive.remaining) : effective.stock}
                  />
                  <button
                    onClick={() => {
                      const cap = limit?.most_restrictive ? Math.min(effective.stock, limit.most_restrictive.remaining) : effective.stock;
                      setQuantity(Math.min(cap, quantity + 1));
                    }}
                    className="qty-btn"
                  >
                    +
                  </button>
                </div>
              </div>

              {modifiers.length > 0 && (
                <div className="product-modifiers" style={{ margin: '16px 0', padding: '12px', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                  <h4 style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 600 }}>Customization</h4>
                  {modifiers.map(m => (
                    <div key={m.id} style={{ marginBottom: 10 }}>
                      <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>
                        {m.label}{m.required ? ' *' : ''}
                        {Number(m.surcharge_cents) > 0 && (
                          <span style={{ color: '#6b7280', marginLeft: 6 }}>
                            (+${(Number(m.surcharge_cents) / 100).toFixed(2)})
                          </span>
                        )}
                      </label>
                      <input
                        type="text"
                        value={modifierValues[m.label] || ''}
                        onChange={(e) => setModifierValues(v => ({ ...v, [m.label]: e.target.value }))}
                        placeholder={m.placeholder || ''}
                        maxLength={m.max_chars || 80}
                        required={!!m.required}
                        style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 4 }}
                      />
                      {m.help_text && <small style={{ color: '#6b7280' }}>{m.help_text}</small>}
                    </div>
                  ))}
                  {modifierSurchargeCents > 0 && (
                    <div style={{ fontSize: 13, fontWeight: 600, marginTop: 6 }}>
                      Customization total: +${(modifierSurchargeCents / 100).toFixed(2)}
                    </div>
                  )}
                </div>
              )}

              <div className="action-buttons">
                <button onClick={handleAddToCart} className="btn btn-primary">
                  Add to Cart
                </button>
                <button onClick={handleBuyNow} className="btn btn-success">
                  Buy Now
                </button>
                {product.preauth_enabled === 1 && Number(product.preauth_amount) > 0 && (
                  <button
                    onClick={handleReserveWithDeposit}
                    className="btn btn-outline"
                    title="Place a deposit to reserve this item — balance is charged when stock ships"
                    style={{ borderColor: '#b45309', color: '#b45309' }}
                  >
                    Reserve with ${Number(product.preauth_amount).toFixed(2)} deposit
                  </button>
                )}
              </div>
              {product.preauth_enabled === 1 && Number(product.preauth_amount) > 0 && (
                <p style={{ marginTop: 8, fontSize: 12, color: '#64748b' }}>
                  Deposit holds your reservation. Remaining balance (${(Number(effective.price) - Number(product.preauth_amount)).toFixed(2)}) will be charged when the product ships.
                </p>
              )}
            </div>
            )
          )}
        </div>
      </div>
      <LastSold productId={product.id} />
      <PriceHistory productId={product.id} currentPrice={effective.price} />
      <FrequentlyBoughtTogether productId={product.id} />
      <ProductReviews productId={product.id} user={null} />
    </div>
  );
};

export default ProductDetail;
